import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['input', 'value', 'menu', 'item', 'notFound', 'caption', 'clear']

    declare readonly inputTarget: HTMLInputElement
    declare readonly hasInputTarget: boolean
    declare readonly valueTarget: HTMLInputElement
    declare readonly hasValueTarget: boolean
    declare readonly itemTargets: HTMLElement[]
    declare readonly notFoundTarget: HTMLElement
    declare readonly hasNotFoundTarget: boolean
    declare readonly captionTarget: HTMLElement
    declare readonly hasCaptionTarget: boolean

    connect() {
        this.element.addEventListener('select:add-error', this.handleAddError as EventListener)
        this.element.addEventListener('select:clear-error', this.handleClearError as EventListener)
        this.element.addEventListener('select:set-loading', this.handleSetLoading as EventListener)
        this.element.addEventListener('select:set-success', this.handleSetSuccess as EventListener)

        this.resolveValue()?.addEventListener('change', this.handleChange as EventListener)
    }

    disconnect() {
        this.element.removeEventListener('select:add-error', this.handleAddError as EventListener)
        this.element.removeEventListener('select:clear-error', this.handleClearError as EventListener)
        this.element.removeEventListener('select:set-loading', this.handleSetLoading as EventListener)
        this.element.removeEventListener('select:set-success', this.handleSetSuccess as EventListener)

        this.resolveValue()?.removeEventListener('change', this.handleChange as EventListener)
    }

    filter() {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        const query = (input.value || '').toLowerCase()
        let found = false

        this.itemTargets.forEach((el) => {
            const match = el.textContent?.trim().toLowerCase().includes(query)

            if (match) {
                found = true
                el.classList.remove('hidden')
            } else {
                el.classList.add('hidden')
            }
        })

        if (this.hasNotFoundTarget) {
            this.notFoundTarget.classList.toggle('hidden', found)
        }
    }

    validate() {
        const input = this.resolveInput()
        const valueInput = this.resolveValue()

        if (!input || !valueInput) {
            return
        }

        const text = input.value?.toLowerCase() || ''

        let equalText = ''
        let equalValue = ''

        this.itemTargets.forEach((el) => {
            if (el.classList.contains('not-found')) {
                return
            }

            const t = el.textContent?.trim() || ''

            if (t.toLowerCase() === text) {
                equalText = t
                equalValue = (el as HTMLLIElement).dataset.value || ''
            }
        })

        if (!equalText || !equalValue) {
            input.value = ''
            valueInput.value = ''
            return
        }

        input.value = equalText
        valueInput.value = equalValue
    }

    select(event: Event) {
        const target = event.target as HTMLElement

        if (!target || !target.classList.contains('with-dropdown__menu--item')) {
            return
        }

        const input = this.resolveInput()
        const valueInput = this.resolveValue()

        if (!input || !valueInput) {
            return
        }

        const textValue = target.textContent?.trim() || ''
        const value = (target as HTMLLIElement).dataset.value || ''

        input.value = textValue
        valueInput.value = value

        valueInput.dispatchEvent(new Event('input', { bubbles: true }))
        valueInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    replaceOptions(options: Array<{ value: string; label: string }>): void {
        const menu = this.element.querySelector('.with-dropdown__menu')

        if (!menu) {
            return
        }

        menu.querySelectorAll('.with-dropdown__menu--item:not(.not-found)').forEach(el => el.remove())

        options.forEach(opt => {
            const li = document.createElement('li')
            li.className = 'with-dropdown__menu--item'
            li.dataset.value = opt.value
            li.textContent = opt.label
            menu.appendChild(li)
        })

        if (this.hasNotFoundTarget) {
            this.notFoundTarget.classList.add('hidden')
        }
    }

    clear() {
        const input = this.resolveInput()
        const valueInput = this.resolveValue()

        if (!input || !valueInput) {
            return
        }

        input.value = ''
        valueInput.value = ''

        valueInput.dispatchEvent(new Event('input', { bubbles: true }))
        valueInput.dispatchEvent(new Event('change', { bubbles: true }))

        this.filter()
    }

    addError(message: string) {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.remove('success', 'loading')
        input.classList.add('error')

        if (this.hasCaptionTarget) {
            this.captionTarget.textContent = message
        }
    }

    clearError() {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.remove('error')

        if (this.hasCaptionTarget) {
            this.captionTarget.textContent = ''
        }
    }

    setLoading(loading: boolean) {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.toggle('loading', loading)

        if (loading) {
            input.classList.remove('error', 'success')
        }
    }

    setSuccess(success: boolean) {
        const input = this.resolveInput()

        if (!input) {
            return
        }

        input.classList.toggle('success', success)

        if (success) {
            input.classList.remove('error', 'loading')
        }
    }

    private handleChange = () => {
        this.clearError()
    }

    private handleAddError = (event: CustomEvent) => {
        const message = (event.detail && (event.detail.message as string)) || ''

        if (!message) {
            return
        }

        this.addError(message)
    }

    private handleClearError = (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        this.clearError()
    }

    private handleSetSuccess = (event: CustomEvent) => {
        const value = !!(event.detail && event.detail.success)
        this.setSuccess(value)
    }

    private handleSetLoading = (event: CustomEvent) => {
        const value = !!(event.detail && event.detail.loading)
        this.setLoading(value)
    }

    private resolveInput(): HTMLInputElement | null {
        if (this.hasInputTarget) {
            return this.inputTarget
        }

        return this.element.querySelector('input.input') as HTMLInputElement | null
    }

    private resolveValue(): HTMLInputElement | null {
        if (this.hasValueTarget) {
            return this.valueTarget
        }

        return this.element.querySelector('input[type="hidden"]') as HTMLInputElement | null
    }
}
